import React, { useState, useEffect, useCallback } from "react";
import { Linking } from "react-native";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { contactService } from "@/services/contactService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface ContactInputProps {
  value: string | null;
  clientId?: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  placeholder?: string;
  info?: string;
  onChange?: (contactId: string | null) => void;
}

export function ContactInput({
  value,
  clientId,
  state = "editing",
  label = "Contact",
  error,
  placeholder = "Select contact\u2026",
  info,
  onChange,
}: ContactInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleIdlePress = useCallback(() => {
    if (!value) return;
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
    if (currentPanel && currentId) {
      pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
    }
    navigateToEntity("contact", value);
  }, [value, currentParams, pushNavigation, currentEntity, navigateToEntity]);
  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [phoneMap, setPhoneMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const contacts = clientId
          ? await contactService.getContactsByClientId(clientId)
          : await contactService.getContacts();
        if (cancelled) return;
        const phones = new Map<string, string>();
        setOptions(
          contacts.map((c) => {
            if (c.id && c.phone_number) phones.set(c.id, c.phone_number);
            return {
              id: c.id!,
              name: `${c.first_name} ${c.last_name}`,
              subtitle: c.phone_number || c.email_address || undefined,
              avatar: {
                firstName: c.first_name ?? undefined,
                lastName: c.last_name ?? undefined,
                email: c.email_address ?? undefined,
              },
            };
          }),
        );
        setPhoneMap(phones);
      } catch (err) {
        console.error("Failed to load contacts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const selectedPhone = value ? phoneMap.get(value) : undefined;

  const handlePhoneCall = useCallback(() => {
    if (selectedPhone) Linking.openURL(`tel:${selectedPhone}`);
  }, [selectedPhone]);

  const resolvedState: LookupInputState = loading ? "loading" : state;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="account"
      error={error}
      placeholder={placeholder}
      info={info}
      onChange={onChange}
      onIdlePress={handleIdlePress}
      idleAction={selectedPhone ? { icon: "phone-outline", onPress: handlePhoneCall } : undefined}
    />
  );
}
